import React, { useEffect, useState, useRef, useMemo } from 'react';
import { AssetsTaskTableProps, TaskFieldType, PageType } from '../types';
import { useAsyncEffect, useSetState } from 'ahooks';
import { getTaskPage, getPageLowCode, getTaskPropertySettings, getTaskPageCache, getTaskPropertySettingsCache, getPageLowCodeCache } from '@/api/scene';
import { Empty, Spin, ConfigProvider, Space } from 'antd';
import { getMaxFloor, Api as FetchAPI, isMobileEnv } from '@/utils';
import { findAssetCascadeData, getTaskRules, getTaskRulesCache } from '@/api/cascade';
import useMetaStore, { useMeta } from '@/store/useMetaStore';
import useValuesStore, { useValues } from '@/store/useValuesStore';
import StoreContext from '@/store/context';
import zhCN from 'antd/es/locale/zh_CN';
import { RuleTigger, AbilityPoint } from '@/const';
import { usePlatform, useStoreInit } from '@/effects';
import { Rule } from '@/store/type';
import * as notify from '@/utils/notify';
import { loadFreeJs } from '@/components/sp-business-btns/effect/useFreeJs';
import { useEventCenter } from '@/asset/AssetTask/templates/Cascade/effect';
import useRowSelection from './effect/useRowSelection';
import { useRowClick } from './effect/useRowClick';
import { listPageRuleFilter } from './service/listPageRuleFilter';
import SPTableWithModel from '@/components/SPTableWithModel';
import SpBusinessBtns from '@/components/sp-business-btns';
import OfficialRow from './components/OfficialRow';
import OfficialHeader from './components/OfficialHeader';
import RuleController from '@/components/RuleController';
import { useJsScript } from '@/asset/AssetTask/templates/Cascade/effect/useJsScript';
import useHeadBtnsList from './effect/useHeadBtnsList';

function AssetTaskTable(props: AssetsTaskTableProps) {
  const {
    taskId,
    recordId,
    utils,
    pageConfig: propsPageConfig,
    onLoad,
    events,
    optionsActionType, // 驾驶舱操作栏打开形式
    filterParams, // 驾驶舱过滤参数使用
    rowSelection: propsRowSelection,
    selectedRowKeys,
    onSelectedRows,
    qiankun,
    isMobile = isMobileEnv(),
    auth,
  } = props;
  (FetchAPI as any).headers = { taskId };
  // 与应用搭建平台通信
  usePlatform({ onLoad, props });
  const meta = useMetaStore();
  const { setMetaInfo, metaInfoByRule, dispatchEvent, moduleList } = meta;
  const { setRules, resetValue, values, pageRules } = useValuesStore();

  const { isReady } = useStoreInit({
    taskId,
    pageType: PageType.List,
    pageConfig: propsPageConfig,
    isMobile,
    utils,
    recordId,
    events,
    auth,
  });
  console.log('Values', values, pageRules);
  console.log('Meta', meta, metaInfoByRule);
  // console.log('Rules', rules);

  const { taskInfo, pageConfig } = meta;
  // 模型数据定义
  const moduleMeta = taskInfo?.modules?.find((module: Record<string, any>) => module.moduleID === taskInfo.mainModuleId) ?? {};
  // 当前模型配置信息
  const moduleConfig = pageConfig?.pageModuleConfig?.[moduleMeta.handle];

  // const fieldConfig = moduleList.find(x => x.handle === moduleMeta.handle)?.fields;
  let fieldConfig = moduleConfig?.configList.map((field: Record<string, any>) => {
    const fieldCfg = moduleMeta.fields?.find((item) => item.fieldID === field.fieldID);
    // 配置中字段还存在，但是逻辑实体上面已经没有该字段了
    if (!fieldCfg) {
      notify.error(notify.Type.FieldNotExistInLogic, { name: field.name, label: field.label });
      return null;
    }
    return {
      dataIndex: fieldCfg.name,
      fieldID: fieldCfg.fieldID,
      search: field?.showSearch || false,
      visible: field?.showSource || false,
      searchOptions: field?.searchOptions,
      width: field?.showOptions?.width,
    };
  });
  fieldConfig = fieldConfig?.filter((x) => !!x);
  // 搜索项
  const searchColumns = fieldConfig?.filter((x: { search: any }) => x.search) || [];
  // 列表项
  const tableColumns = fieldConfig?.filter((x: { visible: any; search: any }) => x.visible || x.search) || [];


  // sp list entry
  const recordInfo = {
    nsLabel: moduleMeta.namespaceHandle,
    moduleLabel: moduleMeta.handle,
    fieldLabel: '',
    fieldConfig: tableColumns,
  };
  const refList = useRef<any>(new Map());
  const tableWithRef = useRef<any>();
  const columns = recordInfo.fieldConfig || [];

  // 缓存选中的行
  const [selectedRows, setSelectedRows] = useState<any[]>();

  const [searchParams, setSearchParams] = useState<Record<string, string>>();
  const selectType = pageConfig?.listPageConfig?.selectType || 'none';
  const showTableIndex = pageConfig?.listPageConfig?.showTableIndex;

  // 加入自定义js按钮
  const freeButtonClick = ({ btnInfo, open }, ctx = {}) => {
    const text = btnInfo?.freeJs;
    loadFreeJs(
      {
        open,
        buiNavigate: utils?.buiNavigate,
        reload: realoadValue,
      },
      {
        taskId,
        utils,
        queryParams: utils?.queryParams,
        selectedRows,
        setSelectedRowKeys,
        btnInfo,
        refList,
        _window: window,
        ...ctx,
        rowData: { ...(ctx?.rowData || {}), RECORD: { recordID: ctx?.rowData?._id } },
      },
      text,
    );
  };

  const extraButtonList = (pageConfig)?.freeBtnConfig?.filter((x) => x.visible !== false) || [];
  useJsScript();
  // 筛选自定义按钮
  const rowFreeBtns = useMemo(() => {
    return extraButtonList
      .filter((item) => item.position === 'module')
      .filter((item) => item['position-direction'] === 'row')
      .filter((item) => item['position-module'] === recordInfo.moduleLabel);
  }, [extraButtonList, recordInfo.moduleLabel]);

  const topFreeBtns = useMemo(() => {
    return extraButtonList
      .filter((item) => item.position === 'module')
      .filter((item) => item['position-direction'] === 'top')
      .filter((item) => item['position-module'] === recordInfo.moduleLabel);
  }, [extraButtonList, recordInfo.moduleLabel]);

  const pageBottomFreeBtns = useMemo(() => {
    return extraButtonList.filter((item) => item.position === 'pageBottom');
  }, [extraButtonList]);

  useEffect(() => {
    refList.current.set(
      `${recordInfo?.moduleLabel},undefined`,
      tableWithRef.current,
    );
  }, []);

  useEventCenter();

  const rowClickLink = pageConfig?.listPageConfig?.rowClickLink || false;

  // 是开开启复选单选
  const { rowSelection, setSelectedRowKeys } = useRowSelection({ selectType, onSelectedRows, selectedRowKeys }, setSelectedRows);
  // 点击行
  const { rowClick } = useRowClick({
    rowClickLink,
    taskId,
    utils,
  });

  useEffect(() => {
    const params = JSON.parse(utils?.queryParams?.filterParams || '{}');
    setSearchParams(params);
  }, [utils?.queryParams?.filterParams]);

  // 取数示例：/{服务名称}/contract?filterParams=%7B"inner_order_no"%3A"0"%2C"current"%3A1%2C"pageSize"%3A20%7D
  const searchStage = (value: Record<string, string>) => {
    utils?.setSearchParams?.({
      ...(utils?.queryParams || {}),
      filterParams: JSON.stringify(value),
    });
  };

  const headerBtns = pageConfig?.listPageConfig?.headerBtns ?? [];
  const moduleInfo = moduleMeta;
  const openVersion = utils?.queryParams?.openVersion === 'true' ? true : undefined;
  const rowBtns = pageConfig?.listPageConfig?.rowBtns ?? [];


  const seaPar = useMemo(() => {
    return { ...filterParams, ...searchParams };
  }, [filterParams, searchParams]);


  const realoadValue = () => {
    tableWithRef.current.reload();
  };

  const useHeadBtnsListFun = (otherProps) => {
    return useHeadBtnsList({
      utils,
      taskId,
      headerBtns,
      selectedRows,
      rowSelection,
      setSelectedRows,
      tableWithRef,
      reload: realoadValue,
      isHaveAdd: true,
      optionsActionType: pageConfig?.listPageConfig?.optionsActionType || optionsActionType,
      moduleInfo,
      btnInfo: topFreeBtns,
      btnsClick: (val) => freeButtonClick(val),
      moduleID: recordInfo?.moduleLabel,
      openVersion,
      namespaceID: recordInfo?.nsLabel,
      filter: moduleConfig?.prefilter,
      searchParams,
      sorter: moduleConfig?.sorter,
      isMobile,
      ...otherProps,
    });
  };
  if (!isReady) {
    const spinStyles = {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      background: 'transparent',
    };
    return (
      <div style={spinStyles}>
        <Spin tip="Loading..." size="large" />
      </div>
    );
  }

  return (
    <RuleController moduleCode={recordInfo?.moduleLabel} ability={AbilityPoint.canSearch} displayUnauthorizedInfo>
      <SPTableWithModel
        key={taskId}
        ref={tableWithRef}
        isMobile={isMobile}
        moduleID={recordInfo?.moduleLabel}
        namespaceID={recordInfo?.nsLabel}
        columns={columns}
        search={searchColumns.length !== 0}
        autoRowEditConfig={{
          hideSave: true,
        }}
        headerBtns={pageConfig?.listPageConfig?.headerBtns ?? []}
        selectedRows={selectedRows}
        options={false}
        onSearch={searchStage}
        searchParams={seaPar}
        sorter={moduleConfig?.sorter}
        mobileRows={moduleConfig?.mobileFieldShow}
        optionColumns={{
          title: '操作',
          valueType: 'option',
          dataIndex: 'options',
          fixed: 'right',
          align: 'center',
          render: (_text, record, index) => {
            const { changeRowBtns, changeFreeBtns } = listPageRuleFilter({
              rowBtns,
              record,
              rowFreeBtns,
              listProps: {
                rulePageValue: pageConfig?.rulePageValue,
                recordInfo,
              },
            });
            if (changeRowBtns === 0 && rowFreeBtns.length === 0) return null;
            return (
              <div style={{ display: 'flex', justifyContent: 'center', flexWrap: isMobile ? 'wrap' : undefined }}>
                {changeRowBtns.length !== 0 && (
                  <OfficialRow
                    rowIndex={index}
                    utils={utils}
                    taskId={taskId}
                    record={record}
                    rowBtns={changeRowBtns}
                    tableWithRef={tableWithRef}
                    moduleID={recordInfo?.moduleLabel}
                    openVersion={openVersion}
                    reload={realoadValue}
                    optionsActionType={pageConfig?.listPageConfig?.optionsActionType || optionsActionType}
                    moduleInfo={moduleInfo}
                    qiankun={qiankun}
                  />
                )}
                {rowFreeBtns.length !== 0 && (
                  <SpBusinessBtns
                    key="row"
                    isRow
                    utils={{ ...utils, reload: realoadValue }}
                    btnInfo={changeFreeBtns}
                    btnsClick={(val) =>
                      freeButtonClick(val, { rowData: record })
                    }
                    moduleID={recordInfo?.moduleLabel}
                    record={record}
                    openDrawer={optionsActionType === 'drawer'}
                  />
                )}
              </div>
            );
          },
        }}
        headBtnsList={useHeadBtnsListFun}
        onRow={rowClick}
        openVersion={openVersion}
        filter={moduleConfig?.prefilter}
        title={moduleConfig?.tabName}
        showTableIndex={showTableIndex}
        rowSelection={propsRowSelection || rowSelection}
      />
      <div className="lcp-pro-list-bottom">
        <SpBusinessBtns
          key="bottom"
          utils={utils}
          btnInfo={pageBottomFreeBtns}
          btnsClick={(val) => freeButtonClick(val)}
          openDrawer={optionsActionType === 'drawer'}
        />
      </div>
    </RuleController>
  );
}


export default function StoreContextWarp(props: any) {
  const values = useValues();
  const meta = useMeta();
  return (
    <StoreContext.Provider value={{ values, meta }}>
      <ConfigProvider locale={zhCN}>
        <AssetTaskTable {...props} />
      </ConfigProvider>
    </StoreContext.Provider>
  );
}
